const DEFAULT_MAX_PIE_SLICES = 10;

/** Default dataZoom window: first N categories (bar charts are sorted highest-first). */
export const DATA_ZOOM_INITIAL_VISIBLE = 50;

/** Approximate plot area for a default dashboard chart card. */
const DEFAULT_PLOT_WIDTH = 320;
const EXPANDED_PLOT_WIDTH = 920;
const DEFAULT_PLOT_HEIGHT = 260;
const EXPANDED_PLOT_HEIGHT = 560;

/**
 * Sort rows by the primary value column descending (highest counts first).
 * @param {object[]} rows
 * @param {string} valKey
 */
export function sortRowsByPrimaryValue(rows, valKey) {
    if (!rows?.length || !valKey) return rows ?? [];
    return [...rows].sort((a, b) => (Number(b[valKey]) || 0) - (Number(a[valKey]) || 0));
}

/**
 * Estimate how many category labels can fit without overlap (matches ChartRenderer axis rules).
 * @param {string[]} categories
 * @param {{ horizontal?: boolean, expanded?: boolean }} [opts]
 */
export function estimateMaxVisibleCategories(categories, { horizontal = false, expanded = false } = {}) {
    if (!categories?.length) return Infinity;

    if (horizontal) {
        const plotHeight = expanded ? EXPANDED_PLOT_HEIGHT : DEFAULT_PLOT_HEIGHT;
        return Math.max(5, Math.floor(plotHeight / 20));
    }

    const count = categories.length;
    const maxLen = categories.reduce((max, c) => Math.max(max, String(c ?? '').length), 0);
    const rotated = count > 8;
    const plotWidth = expanded ? EXPANDED_PLOT_WIDTH : DEFAULT_PLOT_WIDTH;

    let slotPx;
    if (rotated) {
        slotPx = maxLen > 24 ? 52 : Math.max(24, Math.min(maxLen * 4, 56));
    } else if (maxLen > 24) {
        slotPx = 72;
    } else {
        slotPx = Math.max(36, maxLen * 6);
    }

    return Math.max(4, Math.floor(plotWidth / slotPx));
}

/** True when category count exceeds estimated readable capacity. */
export function categoriesNeedDataZoom(categories, options = {}) {
    if (!categories?.length) return false;
    return categories.length > estimateMaxVisibleCategories(categories, options);
}

/** Percentage end index for dataZoom to show the first N categories. */
export function dataZoomEndPercent(categoryCount, visibleCount) {
    if (categoryCount <= 0) return 100;
    if (visibleCount >= categoryCount) return 100;
    return Math.min(100, Math.round((visibleCount / categoryCount) * 100));
}

/** dataZoom start/end window: first N categories (bars — highest counts first). */
export function dataZoomWindowForFirst(categoryCount, visibleCount) {
    if (categoryCount <= 0 || visibleCount >= categoryCount) return { start: 0, end: 100 };
    return { start: 0, end: dataZoomEndPercent(categoryCount, visibleCount) };
}

/** dataZoom start/end window: last N categories (time series — most recent dates). */
export function dataZoomWindowForLatest(categoryCount, visibleCount) {
    if (categoryCount <= 0 || visibleCount >= categoryCount) return { start: 0, end: 100 };
    const windowPct = dataZoomEndPercent(categoryCount, visibleCount);
    return { start: 100 - windowPct, end: 100 };
}

/**
 * ECharts dataZoom when categories cannot all fit (slider + inside scroll/zoom).
 * @param {{ categories: string[], horizontal?: boolean, expanded?: boolean, focusLatest?: boolean }} opts
 */
export function buildCategoryDataZoom({
    categories = [],
    horizontal = false,
    expanded = false,
    focusLatest = false,
}) {
    if (!categoriesNeedDataZoom(categories, { horizontal, expanded })) {
        return { dataZoom: undefined, gridExtra: {} };
    }

    const count = categories.length;
    const window = focusLatest
        ? dataZoomWindowForLatest(count, DATA_ZOOM_INITIAL_VISIBLE)
        : dataZoomWindowForFirst(count, DATA_ZOOM_INITIAL_VISIBLE);
    const { start, end } = window;
    const axisKey = horizontal ? 'yAxisIndex' : 'xAxisIndex';
    const sliderSize = expanded ? 20 : 16;

    return {
        dataZoom: [
            {
                type: 'slider',
                show: true,
                [axisKey]: [0],
                start,
                end,
                brushSelect: false,
                ...(horizontal
                    ? { orient: 'vertical', width: sliderSize, right: 4 }
                    : { height: sliderSize, bottom: 4 }),
            },
            {
                type: 'inside',
                [axisKey]: [0],
                start,
                end,
                zoomOnMouseWheel: true,
                moveOnMouseMove: true,
                moveOnMouseWheel: false,
            },
        ],
        gridExtra: horizontal
            ? { right: sliderSize + 12 }
            : { bottom: expanded ? 48 : 40 },
    };
}

/**
 * Keep top N categories by value; merge remainder into "Other".
 * @param {object[]} rows
 * @param {string} catCol
 * @param {string} valCol
 * @param {number} [maxSlices]
 */
export function rollupPieRows(rows, catCol, valCol, maxSlices = DEFAULT_MAX_PIE_SLICES) {
    if (!rows?.length || rows.length <= maxSlices) {
        return [...(rows ?? [])].sort((a, b) => Number(b[valCol]) - Number(a[valCol]));
    }

    const sorted = [...rows].sort((a, b) => Number(b[valCol]) - Number(a[valCol]));
    const top = sorted.slice(0, maxSlices);
    const rest = sorted.slice(maxSlices);
    const otherSum = rest.reduce((sum, r) => sum + (Number(r[valCol]) || 0), 0);
    if (otherSum > 0) {
        top.push({ [catCol]: 'Other', [valCol]: otherSum });
    }
    return top;
}

/**
 * Build nested ECharts treemap nodes from flat parent_key rows.
 * @param {object[]} rows
 * @param {{ idKey: string, parentKey: string, labelKey: string, valueKey: string }} keys
 */
export function buildTreemapTree(rows, keys) {
    const { idKey, parentKey, labelKey, valueKey } = keys;
    if (!rows?.length || !idKey || !parentKey || !labelKey || !valueKey) return [];

    const byId = new Map();
    for (const row of rows) {
        const id = String(row[idKey] ?? '');
        if (!id) continue;
        byId.set(id, {
            id,
            name: String(row[labelKey] ?? id),
            value: Number(row[valueKey]) || 0,
            children: [],
        });
    }

    const placed = new Set();
    const roots = [];
    for (const row of rows) {
        const id = String(row[idKey] ?? '');
        const node = byId.get(id);
        if (!node || placed.has(id)) continue;
        placed.add(id);

        const parent = row[parentKey];
        const parentId = parent == null || parent === '' ? null : String(parent);
        if (parentId && byId.has(parentId)) {
            byId.get(parentId).children.push(node);
        } else {
            roots.push(node);
        }
    }

    const finalize = (node) => {
        if (!node.children?.length) {
            delete node.children;
            return;
        }
        node.children.sort((a, b) => (b.value || 0) - (a.value || 0));
        node.children.forEach(finalize);
        // Branch nodes: value is rolled up at display time for drill-down tiles.
        delete node.value;
    };
    roots.forEach(finalize);
    roots.sort((a, b) => {
        const av = a.value ?? a.children?.reduce((s, c) => s + (c.value || 0), 0) ?? 0;
        const bv = b.value ?? b.children?.reduce((s, c) => s + (c.value || 0), 0) ?? 0;
        return bv - av;
    });
    return roots;
}

/** Deep-clone a treemap node tree for ECharts (avoids mutating cached tree). */
export function cloneTreemapNode(node) {
    if (!node) return null;
    const copy = { id: node.id, name: node.name };
    if (node.value != null) copy.value = node.value;
    if (node.children?.length) {
        copy.children = node.children.map(cloneTreemapNode);
    }
    return copy;
}

/** Find a node by id in a forest of treemap roots. */
export function findTreemapNodeById(roots, id) {
    if (!id) return null;
    for (const root of roots ?? []) {
        if (root.id === id) return root;
        const hit = findTreemapNodeById(root.children, id);
        if (hit) return hit;
    }
    return null;
}

/** Path from a matching root down to targetId (inclusive). */
export function buildTreemapPath(roots, targetId) {
    if (!targetId) return [];
    const walk = (node, trail) => {
        const next = [...trail, node];
        if (node.id === targetId) return next;
        for (const child of node.children ?? []) {
            const found = walk(child, next);
            if (found.length) return found;
        }
        return [];
    };
    for (const root of roots ?? []) {
        const path = walk(root, []);
        if (path.length) return path;
    }
    return [];
}

/** Sum node value, rolling up through branch nodes that omit value. */
export function rollupTreemapValue(node) {
    if (!node) return 0;
    if (node.value != null && !Number.isNaN(Number(node.value))) return Number(node.value);
    return (node.children ?? []).reduce((sum, child) => sum + rollupTreemapValue(child), 0);
}

/** One treemap tile at the current drill level (no nested grandchildren). */
export function toTreemapDisplayNode(node) {
    if (!node) return null;
    const hasChildren = (node.children?.length ?? 0) > 0;
    return {
        id: node.id,
        name: node.name,
        value: hasChildren ? rollupTreemapValue(node) : (Number(node.value) || 0),
    };
}

/** Siblings at the current drill level only — each click reveals the next level. */
export function getTreemapFocusData(roots, focusId) {
    if (!focusId) {
        return (roots ?? []).map(toTreemapDisplayNode).filter(Boolean);
    }
    const node = findTreemapNodeById(roots, focusId);
    if (!node) {
        return (roots ?? []).map(toTreemapDisplayNode).filter(Boolean);
    }
    return (node.children ?? []).map(toTreemapDisplayNode).filter(Boolean);
}
